import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Building2, Stethoscope, Link2, Loader2 } from "lucide-react";
import { mailboxApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";

export function AssignPopover({ email, onAssigned }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => mailboxApi.targets(q).then((r) => setResults(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const assign = async (target) => {
    setBusy(true);
    try {
      const res = await mailboxApi.assign(email.id, { target_type: target.type, target_id: target.id, remember });
      toast.success(`Zugeordnet: ${target.name}${res.data.also_assigned ? ` (+${res.data.also_assigned} weitere)` : ""}`);
      setOpen(false);
      onAssigned?.();
    } catch {
      toast.error("Zuordnung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" data-testid={`mail-assign-btn-${email.id}`}><Link2 className="w-4 h-4 mr-1.5" />Zuordnen</Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3 space-y-2" align="end">
        <Input autoFocus placeholder="Kunde oder Hersteller suchen…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="mail-assign-search" />
        <div className="max-h-64 overflow-y-auto -mx-1">
          {results.map((t) => (
            <button key={`${t.type}-${t.id}`} disabled={busy} onClick={() => assign(t)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm hover:bg-muted" data-testid={`mail-assign-target-${t.type}-${t.id}`}>
              {t.type === "vendor" ? <Stethoscope className="w-4 h-4 text-primary shrink-0" /> : <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />}
              <span className="flex-1 min-w-0 truncate">{t.name}</span>
              {t.hint && <span className="text-xs text-muted-foreground shrink-0">{t.hint}</span>}
            </button>
          ))}
          {results.length === 0 && <p className="text-sm text-muted-foreground px-2 py-3">Keine Treffer.</p>}
        </div>
        {email.counterpart && (
          <label className="flex items-start gap-2 text-xs text-muted-foreground pt-1 border-t">
            <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} className="mt-0.5" data-testid="mail-assign-remember" />
            <span>Mails von/an <b>{email.counterpart}</b> künftig automatisch hier zuordnen</span>
          </label>
        )}
        {busy && <Loader2 className="w-4 h-4 animate-spin mx-auto" />}
      </PopoverContent>
    </Popover>
  );
}
